import MarketplaceProductCard, {
  CAROUSEL_CARD_WIDTH,
} from '@c/marketplace/MarketplaceProductCard';
import { useQuery } from '@tanstack/react-query';
import ButtonToggle from '@ui/ButtonToggle';
import Carousel from '@ui/Carousel';
import PageSectionHeader from '@ui/PageSectionHeader';
import Select from '@ui/Select';
import Spinner from '@ui/Spinner';
import { CategoryDocument } from '@util/firestore/categories';
import {
  getCuratedListByCategory
} from '@util/firestore/products';
import useProductsPage, {
  ListingsFilters,
  PaginationFilters,
} from '@util/hooks/useProductsPage';
import { ProductDocument } from '@util/types/firestore/products';
import { Pagination } from '@util/types/paginator';
import ProductFilterDrawer from './drawers/ProductFilterDrawer';
import ListingFilters from './filters/ListingFilters';
import CategoryPageHeader from './headers/CategoryPageHeader';
import { PageMainContent } from './products/PageMainContent';
import { itemsPerPageOptions, sortingOptions } from './ProductsPage';
import FAQContent from './FAQContentSection';
import { ConditionDocument } from '@util/types/firestore/conditions';
import { BrandDocument } from '@util/types/firestore/brand';
import Breadcrumb, { BreadcrumbItem } from '@ui/Breadcrumb';

export type CategoryPageSideFilters = ListingsFilters;

export type CategoryOrSubcategoryPageProps = {
  slugToTitle: string | undefined;
  results: ProductDocument[];
  pageInfo: Pagination;
  pageHeadline?: string;
  content?: string;
  initialTopFilters: PaginationFilters;
  initialSideFilters: CategoryPageSideFilters;
  keyword: string | null;
  curated_list?: string | string[] | null;
  seller_id: string | null;
  brandOptions: BrandDocument[];
  conditionOptions: ConditionDocument[];
  categoryOptions: CategoryDocument[];
  useClient?: boolean
};

export default function CategoryOrSubcategoryPage({
  keyword,
  slugToTitle,
  results,
  pageInfo: initialPageInfo,
  initialTopFilters,
  initialSideFilters,
  brandOptions,
  conditionOptions,
  categoryOptions,
  curated_list
}: CategoryOrSubcategoryPageProps) {
  const {
    view,
    buttonToggles,
    isFetching,
    products,
    sideFilters,
    topFilters,
    handleSideFiltersChange,
    handleLimitChange,
    handleSortChange,
    handlePageChange,
    drawerOpen,
    setDrawerOpen,
    pageInfo: livePageInfo
  } = useProductsPage({
    results,
    pageInfo: initialPageInfo,
    initialTopFilters,
    initialSideFilters,
    slugToTitle,
    keyword,
    seller_id: null,
    brandOptions,
    conditionOptions,
    categoryOptions,
    curated_list
  });

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: 'Home', href: '/' },
    { label: 'Products', href: '/products' },
    { label: sideFilters.category?.name || '', href: `/c/${sideFilters.category?.slug}` },
  ];

  const { data: staffPicks } = useQuery(
    ['staffPicks', sideFilters.category],
    () => getCuratedListByCategory('staff-picks', sideFilters.category?.name, 5),
    {
      enabled: !!sideFilters.category,
    }
  );

  return (
    <>
      <CategoryPageHeader
        category={sideFilters.category}
        searchTitle={
          keyword ? `${livePageInfo.totalResults} Results for "${keyword}"` :
          (!sideFilters.category && slugToTitle) ? slugToTitle : null
        }
      />

      <div className="
        w-full max-w-default pt-[2rem]
        px-[2rem] md:px-[4rem] xl:px-default-padding-x
      ">
        <PageMainContent
          view={view}
          isFetching={isFetching}
          products={products}
          pageInfo={livePageInfo}
          // CategoryOrSubcategoryPage is also rendered by the Black Friday /
          // Cyber Monday landing page (pages/c/black-friday-cyber-monday.tsx),
          // which always passes category: null. Gate on initialSideFilters
          // (the SSR-set prop, stable for the life of the page) rather than
          // sideFilters (live client state from useProductsPage) — the
          // latter changes when a visitor picks a category from the filter
          // drawer, which would flip master links back on mid-session on a
          // route that must never show them (Codex finding, GB web#1005
          // gate round 2). Restored here after going dark in the homepage
          // redesign promotion (commit 43cfac487); the port now targets
          // MarketplaceProductCard.tsx instead of the retired ProductCard.tsx.
          showMasterLinks={!!initialSideFilters.category}
          filters={
            <ListingFilters
              categoryOptions={categoryOptions}
              brandOptions={brandOptions}
              conditionOptions={conditionOptions}
              initialFilters={sideFilters}
              onFiltersChange={handleSideFiltersChange}
              onOpenDrawer={() => setDrawerOpen(true)}
            />
          }
          controls={
            <div className="flex w-full gap-[1rem] justify-between">
              <Breadcrumb items={breadcrumbItems} />
              <div className="flex gap-[1.6rem]">
                <Select
                  value={topFilters.sort}
                  options={sortingOptions}
                  onChange={handleSortChange}
                  allowEmpty={false}
                />
                <Select
                  value={topFilters.limit.toString()}
                  options={itemsPerPageOptions}
                  onChange={handleLimitChange}
                  allowEmpty={false}
                />
                <ButtonToggle buttons={buttonToggles} />
              </div>
            </div>
          }
          onPageChange={(page: number) => {
            handlePageChange(page)
            console.log("Changed to page ", page)
          }}
        />
      </div>
     
      <div className="
        flex flex-col 
        w-full max-w-default
        gap-[1.2rem]
        my-[4rem]
        px-[2rem] md:px-[4rem] xl:px-default-padding-x
      ">
        
        {(initialSideFilters.category?.description || initialSideFilters.category?.long_description) && (
          <div className="
            flex flex-col 
            justify-between gap-[1.6rem]
            overflow-x-hidden
            p-[2rem] sm:p-[2.5rem]
            border-[1px] border-brand-light-gray rounded-[10px]
          ">
            <span className="text-[16px] primary-links" dangerouslySetInnerHTML={{ __html: initialSideFilters.category.long_description || initialSideFilters.category.description || "" }} />
          </div>
        )}

        {!staffPicks && sideFilters.category ? (
          <div className="pt-[2rem] m-auto h-[4rem] w-[4rem]">
            <Spinner />
          </div>
        ) : staffPicks && staffPicks.length ? (
          <div className="pt-[2rem]">
            <PageSectionHeader
              title="Staff Picks"
              href="/products"
              linkText="See All"
            />
            <Carousel
              items={staffPicks.map((result, i) => (
                <MarketplaceProductCard
                  item={result}
                  key={i}
                  className={CAROUSEL_CARD_WIDTH}
                  // Restored: the legacy ProductCard opened Staff Picks in a new
                  // tab so a click did not lose the shopper's place in the
                  // category they were browsing.
                  newTab
                />
              ))}
            />
          </div>
        ) : null}

        <FAQContent content={initialSideFilters.category?.faq} emitFaqPageMicrodata={false} />
      </div>

      <ProductFilterDrawer
        conditionOptions={conditionOptions}
        categoryOptions={categoryOptions}
        brandOptions={brandOptions}
        open={drawerOpen}
        dismiss={() => {
          setDrawerOpen(false);
        }}
        initialFilters={sideFilters}
        onFiltersChange={handleSideFiltersChange}
      />
    </>
  );
}
